import { useCallback, useEffect, useState } from "react";
import { Bell } from "lucide-react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const TABS = [
  ["applications", "Applications"],
  ["contacts", "Contacts"],
  ["broker_submissions", "Broker deals"],
  ["investor_requests", "Investor requests"],
  ["expert_applications", "Expert apps"],
  ["meetings", "Meetings"],
  ["underwriting_sessions", "AI chats"],
  ["developer_leads", "Developer leads"],
  ["pages", "Site pages"],
];

const PAGE_GROUPS = [
  {
    group: "Campaign landing pages — for ads, not indexed",
    items: [
      { t: "Development finance — indicative terms in days", en: "/lp/finance", pt: "/pt/lp/finance" },
      { t: "Pre-construction finance — land, licensing, pre-sales", en: "/lp/pre-construction", pt: "/pt/lp/pre-construction" },
      { t: "Invest — 8% p.a. secured on Portuguese property", en: "/lp/investors", pt: "/pt/lp/investors" },
    ],
  },
  {
    group: "Main site",
    items: [
      { t: "Homepage", en: "/", pt: "/pt/" },
      { t: "Development finance hub", en: "/development-finance-portugal", pt: "/pt/development-finance-portugal" },
      { t: "Pre-construction finance product", en: "/products/pre-construction-finance", pt: "/pt/products/pre-construction-finance" },
      { t: "AI underwriting application", en: "/apply", pt: "/pt/apply" },
      { t: "Brokers", en: "/brokers", pt: "/pt/brokers" },
      { t: "Broker deal submission", en: "/brokers/submit", pt: "/pt/brokers/submit" },
      { t: "Investors", en: "/investors", pt: "/pt/investors" },
      { t: "Investor information request", en: "/investor-info", pt: "/pt/investor-info" },
      { t: "Expert network", en: "/expert-network", pt: "/pt/expert-network" },
      { t: "About", en: "/about", pt: "/pt/about" },
      { t: "Contact", en: "/contact", pt: "/pt/contact" },
      { t: "Book a meeting", en: "/book", pt: "/pt/book" },
      { t: "FAQ", en: "/faq", pt: "/pt/faq" },
      { t: "Sitemap page", en: "/sitemap", pt: "/pt/sitemap" },
    ],
  },
  {
    group: "Insights",
    items: [
      { t: "Insights hub", en: "/insights", pt: "/pt/insights" },
      { t: "The Complete Guide to Development Finance in Portugal", en: "/insights/development-finance-portugal-guide", pt: "/pt/insights/development-finance-portugal-guide" },
      { t: "How to finance land before planning permission", en: "/insights/finance-land-before-planning", pt: "/pt/insights/finance-land-before-planning" },
      { t: "Bridge loans vs bank construction loans", en: "/insights/bridge-loans-vs-bank-construction-loans", pt: "/pt/insights/bridge-loans-vs-bank-construction-loans" },
      { t: "Why Portuguese banks often finance development later", en: "/insights/why-banks-finance-later", pt: "/pt/insights/why-banks-finance-later" },
    ],
  },
  {
    group: "Legal (English only)",
    enOnly: true,
    items: [
      { t: "Privacy policy", en: "/privacy" },
      { t: "Terms of use", en: "/terms" },
      { t: "Cookies policy", en: "/cookies" },
      { t: "Lending disclaimer", en: "/lending-disclaimer" },
    ],
  },
];

const TOTAL_PAGES = PAGE_GROUPS.reduce((n, g) => n + g.items.length, 0);

const FIELDS = {
  applications: [
    ["Reference", "reference"], ["Developer", "name"], ["Company", "company"],
    ["Email", "email"], ["Phone", "phone"], ["Experience", "experience"],
    ["Location", "location"], ["Type", "project_type"], ["Stage", "stage"],
    ["Amount", "amount_required"], ["Land value", "land_price"],
    ["Project cost", "project_cost"], ["Equity", "equity_invested"],
    ["Planning", "planning_status"], ["Source", "source"], ["Summary", "summary"],
  ],
  contacts: [
    ["Source", "source"], ["Referring URL", "referring_url"], ["Landing page", "landing_page"],
    ["Campaign", "utm"], ["Audience", "audience"], ["Name", "name"], ["Email", "email"], ["Message", "message"],
  ],
  broker_submissions: [
    ["Reference", "reference"], ["Broker", "broker_name"], ["Company", "broker_company"],
    ["Email", "email"], ["Phone", "phone"], ["Developer", "developer_name"],
    ["Location", "project_location"], ["Type", "project_type"], ["Stage", "project_stage"],
    ["Amount", "amount_required"], ["Notes", "notes"], ["Source", "source"],
    ["Referring URL", "referring_url"], ["Landing page", "landing_page"], ["Campaign", "utm"],
  ],
  investor_requests: [
    ["Source", "source"], ["Referring URL", "referring_url"], ["Landing page", "landing_page"],
    ["Campaign", "utm"], ["Ticket", "ticket"], ["Investor type", "investor_type"], ["Name", "name"],
    ["Email", "email"], ["Country", "country"], ["Message", "message"],
  ],
  expert_applications: [
    ["Source", "source"], ["Referring URL", "referring_url"], ["Landing page", "landing_page"],
    ["Campaign", "utm"], ["Name", "name"], ["Email", "email"], ["Specialty", "specialty"],
    ["Location", "location"], ["Notes", "notes"],
  ],
  meetings: [
    ["Source", "source"], ["Referring URL", "referring_url"], ["Landing page", "landing_page"],
    ["Campaign", "utm"], ["Name", "name"], ["Email", "email"], ["Phone", "phone"], ["Date", "date"],
    ["Time", "time"], ["Topic", "topic"], ["Note", "note"], ["Status", "status"],
  ],
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

function primaryOf(doc, coll) {
  if (coll === "applications") return `${doc.reference || ""} — ${doc.name || "—"}`;
  if (coll === "broker_submissions") return `${doc.reference || ""} — ${doc.broker_name || "—"}`;
  if (coll === "underwriting_sessions") {
    if (doc.reference) return doc.reference;
    if (doc.summary) return doc.summary.length > 80 ? `${doc.summary.slice(0, 80)}…` : doc.summary;
    return "Chat without case reference";
  }
  return doc.name || "—";
}

function DetailLine({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex gap-3 text-[13px] leading-relaxed">
      <span className="w-28 shrink-0 font-semibold text-fog">{label}</span>
      <span className="min-w-0 break-words text-charcoal">{value}</span>
    </div>
  );
}

function ChatTranscript({ messages }) {
  if (!Array.isArray(messages) || messages.length === 0) {
    return <p className="text-[13px] text-fog">No messages recorded.</p>;
  }
  return (
    <div className="max-h-[420px] space-y-2 overflow-y-auto rounded-xl bg-cream/60 p-3">
      {messages.map((m, i) => (
        <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
          <div
            className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[13px] leading-relaxed ${
              m.role === "user" ? "bg-verde-800 text-white" : "border border-sand bg-white text-charcoal"
            }`}
          >
            {m.content}
          </div>
        </div>
      ))}
    </div>
  );
}

function CopyBtn({ path, label, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const url = window.location.origin + path;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testid}
      className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
        copied ? "border-verde-800 bg-verde-800 text-white" : "border-sand bg-white text-fog hover:border-verde-800/40 hover:text-verde-800"
      }`}
    >
      {copied ? "Copied ✓" : label}
    </button>
  );
}

function PagesSection() {
  return (
    <div className="space-y-6" data-testid="admin-pages">
      {PAGE_GROUPS.map((g) => (
        <div key={g.group} className="rounded-2xl border border-sand bg-white p-5">
          <p className="font-display text-sm font-bold uppercase tracking-wider text-verde-800">{g.group}</p>
          <div className="mt-4 space-y-3">
            {g.items.map((item) => (
              <div key={item.en} className="flex flex-wrap items-center justify-between gap-3 border-b border-sand/60 pb-3 last:border-0 last:pb-0" data-testid={`admin-page-${item.en}`}>
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-charcoal">{item.t}</p>
                  <p className="mt-0.5 break-all text-[12px] text-fog">
                    {item.en}
                    {item.pt ? ` · ${item.pt}` : ""}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <CopyBtn path={item.en} label="Copy EN" testid={`admin-copy-en-${item.en}`} />
                  {item.pt ? <CopyBtn path={item.pt} label="Copy PT" testid={`admin-copy-pt-${item.pt}`} /> : null}
                  <a
                    href={item.en}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`admin-open-${item.en}`}
                    className="rounded-full border border-sand bg-white px-3 py-1 text-[11px] font-semibold text-verde-800 transition-colors hover:border-verde-800/40"
                  >
                    Open
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Row({ coll, doc, expanded, onExpand, onToggle, onDelete, isNew }) {
  const secondary = doc.email || doc.phone || "—";
  const meta = coll === "underwriting_sessions"
    ? `${doc.lang === "pt" ? "PT" : doc.lang === "nl" ? "NL" : "EN"} · ${doc.messages?.length || 0} ${doc.messages?.length === 1 ? "message" : "messages"}`
    : doc.source || doc.audience || doc.specialty || doc.investor_type || doc.status || doc.tier || "";
  return (
    <div className="rounded-2xl border border-sand bg-white p-4" data-testid={`admin-row-${doc.id}`}>
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={onExpand}
          data-testid={`admin-row-expand-${doc.id}`}
        >
          <p className="font-display text-[15px] font-bold text-charcoal">
            {isNew ? <span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#2F7D4E] align-middle" data-testid={`admin-new-dot-${doc.id}`} aria-label="New lead" /> : null}
            {primaryOf(doc, coll)}
            {doc.handled ? <span className="ml-2 rounded-full bg-verde-800/10 px-2 py-0.5 text-[10px] font-semibold text-verde-800">handled</span> : null}
          </p>
          <p className="mt-0.5 truncate text-[13px] text-fog">
            {secondary} · {fmtDate(doc.created_at)}
            {meta ? ` · ${meta}` : ""}
          </p>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onDelete}
            data-testid={`admin-delete-${doc.id}`}
            className="rounded-full border border-sand bg-white px-3 py-1.5 text-xs font-semibold text-fog transition-colors hover:border-red-700/40 hover:text-red-700"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={() => onToggle(doc)}
            data-testid={`admin-handled-${doc.id}`}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              doc.handled
                ? "border-verde-800 bg-verde-800 text-white"
                : "border-sand bg-white text-fog hover:border-verde-800/40 hover:text-verde-800"
            }`}
          >
            {doc.handled ? "Handled" : "Mark handled"}
          </button>
        </div>
      </div>
      {expanded ? (
        <div className="mt-4 space-y-2 border-t border-sand pt-4" data-testid={`admin-detail-${doc.id}`}>
          {coll === "developer_leads" ? (
            <>
              <DetailLine label="Tier" value={doc.tier} />
              <DetailLine label="Verde fit" value={doc.verde_fit} />
              <DetailLine label="Fit reason" value={doc.fit_reason} />
              <DetailLine label="Track record" value={doc.track_record_1to5 ? `${doc.track_record_1to5}/5` : null} />
              <DetailLine label="HQ" value={doc.hq} />
              <DetailLine label="Regions" value={doc.regions} />
              <DetailLine label="Founded" value={doc.founded} />
              <DetailLine label="Years active" value={doc.years} />
              <DetailLine label="Completed" value={doc.completed} />
              <DetailLine label="Delivered" value={doc.delivered} />
              <DetailLine label="Notable projects" value={doc.notable} />
              <DetailLine label="Pipeline" value={doc.pipeline} />
              <DetailLine label="Segments" value={doc.segments} />
              <DetailLine label="Typical size" value={doc.size} />
              <DetailLine label="Backers" value={doc.backers} />
              <DetailLine label="Key people" value={doc.people} />
              <DetailLine label="Group" value={doc.group} />
              <DetailLine label="Origin" value={doc.origin} />
              <div className="flex gap-3 text-[13px] leading-relaxed">
                <span className="w-28 shrink-0 font-semibold text-fog">Contact</span>
                <span className="flex min-w-0 flex-wrap gap-x-3 gap-y-1">
                  {doc.email ? <a href={`mailto:${doc.email}`} data-testid={`dev-lead-email-${doc.id}`} className="break-all text-verde-800 underline">{doc.email}</a> : null}
                  {doc.phone ? <a href={`tel:${doc.phone.replace(/\s/g, "")}`} className="text-verde-800 underline">{doc.phone}</a> : null}
                  {doc.website ? <a href={doc.website} target="_blank" rel="noreferrer" className="break-all text-verde-800 underline">{doc.website.replace(/^https?:\/\//, "")}</a> : null}
                  {doc.linkedin ? <a href={doc.linkedin} target="_blank" rel="noreferrer" className="text-verde-800 underline">LinkedIn</a> : null}
                  {!doc.email && !doc.phone && !doc.website && !doc.linkedin ? <span className="text-fog">—</span> : null}
                </span>
              </div>
              {Array.isArray(doc.sources) && doc.sources.length > 0 ? (
                <div className="flex gap-3 text-[13px] leading-relaxed">
                  <span className="w-28 shrink-0 font-semibold text-fog">Sources</span>
                  <span className="flex min-w-0 flex-col gap-1">
                    {doc.sources.map((s) => (
                      <a key={s} href={s} target="_blank" rel="noreferrer" className="break-all text-verde-800 underline">
                        {s.replace(/^https?:\/\/(www\.)?/, "").slice(0, 60)}
                      </a>
                    ))}
                  </span>
                </div>
              ) : null}
            </>
          ) : coll === "underwriting_sessions" ? (
            <>
              {doc.summary ? <p className="text-[13px] leading-relaxed text-charcoal">{doc.summary}</p> : null}
              <DetailLine label="Language" value={doc.lang} />
              <DetailLine label="Progress" value={doc.progress} />
              {doc.fields && typeof doc.fields === "object"
                ? Object.entries(doc.fields).map(([k, v]) => <DetailLine key={k} label={k} value={String(v)} />)
                : null}
              {Array.isArray(doc.documents) && doc.documents.length > 0 ? (
                <DetailLine label="Documents" value={doc.documents.map((d) => d.name || d).join(", ")} />
              ) : null}
              <ChatTranscript messages={doc.messages} />
            </>
          ) : (
            (FIELDS[coll] || []).map(([label, key]) => <DetailLine key={key} label={label} value={doc[key]} />)
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function AdminInbox() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("contacts");
  const [inbox, setInbox] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [newSince, setNewSince] = useState(null);
  const [bellOpen, setBellOpen] = useState(false);

  useEffect(() => {
    document.title = "Admin Inbox — Verde Capital";
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/admin/inbox`, { credentials: "include" });
      if (r.status === 401) {
        setAuthed(false);
        return;
      }
      const data = await r.json();
      setInbox(data);
      setNewSince(data.new_since || null);
      setAuthed(true);
    } catch {
      setError("Could not reach the server.");
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await fetch(`${API_BASE}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ passcode }),
      });
      if (r.status === 200) {
        const d = await r.json().catch(() => ({}));
        setNewSince(d.new_since || null);
        setPasscode("");
        load();
      } else {
        const d = await r.json().catch(() => ({}));
        setError(d.detail || "Login failed.");
      }
    } catch {
      setError("Could not reach the server.");
    }
  };

  const logout = async () => {
    await fetch(`${API_BASE}/admin/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    setInbox(null);
    setAuthed(false);
  };

  const toggle = async (doc) => {
    const next = !doc.handled;
    setInbox((prev) => ({
      ...prev,
      [tab]: prev[tab].map((d) => (d.id === doc.id ? { ...d, handled: next } : d)),
    }));
    const r = await fetch(`${API_BASE}/admin/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ collection: tab, id: doc.id, handled: next }),
    }).catch(() => null);
    if (!r || r.status !== 200) {
      setInbox((prev) => ({
        ...prev,
        [tab]: prev[tab].map((d) => (d.id === doc.id ? { ...d, handled: !next } : d)),
      }));
    }
  };

  const remove = async (doc) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    const r = await fetch(`${API_BASE}/admin/delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ collection: tab, id: doc.id }),
    }).catch(() => null);
    if (r && r.status === 200) {
      setInbox((prev) => ({ ...prev, [tab]: prev[tab].filter((d) => d.id !== doc.id) }));
      setBellOpen(false);
    }
  };

  const isNewLead = (d) => !!newSince && !!d.created_at && new Date(d.created_at) > new Date(newSince);
  const [devSearch, setDevSearch] = useState("");
  const newLeads = inbox
    ? TABS.filter(([key]) => key !== "developer_leads")
        .flatMap(([key, label]) => (inbox[key] || []).filter(isNewLead).map((d) => ({ ...d, coll: key, label })))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    : [];

  if (authed === null) {
    return <div className="min-h-screen bg-cream" />;
  }

  if (!authed) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-cream px-5 py-24">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-sand bg-white p-8" data-testid="admin-login">
          <p className="font-display text-2xl font-bold text-charcoal">Admin Inbox</p>
          <p className="mt-1 text-sm text-fog">Enter the team passcode to continue.</p>
          <input
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Passcode"
            data-testid="admin-passcode-input"
            className="mt-5 w-full rounded-xl border border-sand bg-white px-4 py-3 text-base text-charcoal outline-none focus:border-verde-800"
            autoComplete="current-password"
          />
          {error ? <p className="mt-2 text-[13px] text-red-700" data-testid="admin-error">{error}</p> : null}
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="mt-4 w-full rounded-full bg-verde-800 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-verde-900"
          >
            Sign in
          </button>
        </form>
      </div>
    );
  }

  const rawList = inbox?.[tab] || [];
  const list =
    tab === "developer_leads" && devSearch.trim()
      ? rawList.filter((d) =>
          [d.name, d.hq, d.regions, d.segments, d.tier, d.verde_fit, d.people, d.email, d.notable, d.pipeline]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(devSearch.trim().toLowerCase()),
        )
      : rawList;
  const openCount = list.filter((d) => !d.handled).length;

  return (
    <div className="min-h-[80vh] bg-cream px-5 py-24 sm:px-8 lg:px-12" data-testid="admin-inbox">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="eyebrow">Verde Capital · internal</p>
            <h1 className="mt-2 font-display text-3xl font-bold text-charcoal sm:text-4xl">Admin Inbox</h1>
            <p className="mt-1 text-sm text-fog">Every lead lands here automatically — applications, contacts, deals, requests and AI chats.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setBellOpen(!bellOpen)}
                data-testid="admin-bell"
                className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-sand bg-white text-verde-800 transition-colors hover:border-verde-800/40"
                aria-label="New leads since last login"
              >
                <Bell className="h-4.5 w-4.5" />
                {newLeads.length > 0 && (
                  <span data-testid="admin-bell-count" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#2F7D4E] px-1 text-[11px] font-bold text-white">
                    {newLeads.length}
                  </span>
                )}
              </button>
              {bellOpen && (
                <div data-testid="admin-bell-panel" className="absolute right-0 top-12 z-20 w-80 rounded-2xl border border-sand bg-white p-3 shadow-xl">
                  <p className="px-2 pb-2 text-[11px] font-bold uppercase tracking-wider text-fog">New since your last login</p>
                  {newLeads.length === 0 ? (
                    <p className="px-2 py-3 text-sm text-fog">No new leads.</p>
                  ) : (
                    <div className="max-h-72 space-y-1 overflow-y-auto">
                      {newLeads.map((d) => (
                        <button
                          key={d.coll + d.id}
                          type="button"
                          onClick={() => { setTab(d.coll); setBellOpen(false); }}
                          data-testid={`admin-bell-item-${d.id}`}
                          className="block w-full rounded-xl px-2 py-2 text-left transition-colors hover:bg-cream/70"
                        >
                          <span className="block text-[10px] font-bold uppercase tracking-wider text-verde-800">{d.label}</span>
                          <span className="block truncate text-[13px] font-semibold text-charcoal">{primaryOf(d, d.coll)}</span>
                          <span className="block text-[11px] text-fog">{fmtDate(d.created_at)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={load}
              data-testid="admin-refresh"
              className="rounded-full border border-sand bg-white px-4 py-2 text-xs font-semibold text-charcoal transition-colors hover:border-verde-800/40"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout"
              className="rounded-full border border-sand bg-white px-4 py-2 text-xs font-semibold text-charcoal transition-colors hover:border-verde-800/40"
            >
              Sign out
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map(([key, label]) => {
            const count = key === "pages" ? TOTAL_PAGES : (inbox?.[key]?.length ?? 0);
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => { setTab(key); setExpanded(null); }}
                data-testid={`admin-tab-${key}`}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                  active
                    ? "border-verde-800 bg-verde-800 text-white"
                    : "border-sand bg-white text-fog hover:border-verde-800/40 hover:text-verde-800"
                }`}
              >
                {label} <span className={active ? "text-white/70" : "text-fog/70"}>({count})</span>
              </button>
            );
          })}
        </div>

        {tab === "pages" ? (
          <PagesSection />
        ) : (
          <>
            {openCount > 0 ? (
              <p className="mt-4 text-[13px] text-fog" data-testid="admin-open-count">
                {openCount} not yet handled in this tab.
              </p>
            ) : null}

            {tab === "developer_leads" ? (
              <input
                type="search"
                value={devSearch}
                onChange={(e) => setDevSearch(e.target.value)}
                placeholder="Search by name, region, segment, people…"
                data-testid="admin-dev-leads-search"
                className="mt-4 w-full rounded-xl border border-sand bg-white px-4 py-2.5 text-sm text-charcoal outline-none focus:border-verde-800"
              />
            ) : null}

            <div className="mt-4 space-y-3" data-testid={`admin-list-${tab}`}>
              {list.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-sand bg-white p-8 text-center text-sm text-fog">
                  Nothing here yet.
                </p>
              ) : (
                list.map((doc) => (
                  <Row
                    key={doc.id}
                    coll={tab}
                    doc={doc}
                    expanded={expanded === doc.id}
                    onExpand={() => setExpanded(expanded === doc.id ? null : doc.id)}
                    onToggle={toggle}
                    onDelete={() => remove(doc)}
                    isNew={isNewLead(doc)}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}